<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import { storeToRefs } from 'pinia';
import OverlayPanel from 'primevue/overlaypanel';
import type { CartAnimal } from '../../../interfaces';
import { useAToast } from '@Atoms';
import { applyShelterCurrency } from '@Helpers';
import { INTAKE_TYPES } from '@IntakeModule';
import { OUTCOMES_TYPES } from '../../../enums';
import { EditAnimalFeeService } from '../../../services';
import useShoppingCartStore from '../../../store';

const props = defineProps<{
  animal: CartAnimal;
  isLastAnimal: boolean;
  isEditingAnimals: boolean;
}>();

const store = useShoppingCartStore();
const { cartData, loading } = storeToRefs(store);
const { notify, TOAST_TYPES } = useAToast();

const TOAST_ERROR = {
  message: 'cart.toast.medicalCosts.error.message',
  title: 'cart.toast.medicalCosts.error.title',
  type: TOAST_TYPES.ERROR,
};

const serviceFeeOverlay = useTemplateRef<InstanceType<typeof OverlayPanel> | null>('serviceFeeOverlay');
const editAmountRef = useTemplateRef<{ closeEdit: () => void } | null>('editAmount');
const feeLabelKey = computed(() => getFeeLabelKey(props.animal?.outcome_type));
const hideFeeAndPromo = computed(() =>
  [OUTCOMES_TYPES.SERVICE_OUT, OUTCOMES_TYPES.CLINIC_OUT].includes(props.animal?.outcome_type as OUTCOMES_TYPES)
);
const feeAmountNumber = computed(() => parseFloat(String(originalFeeAmount.value ?? 0)) || 0);

const feeOriginalForDisplay = computed(() => {
  const isFeeEdited = props.animal?.is_fee_edited;
  if (!isFeeEdited || props.animal?.original_fee == null) return null;

  const original = parseFloat(String(props.animal.original_fee)) || 0;
  const current = parseFloat(String(originalFeeAmount.value ?? 0)) || 0;
  return original > current ? original : null;
});

const originalFeeAmount = computed(() =>
  props.animal?.outcome_type === 'return_to_owner' ? props.animal.rto_fee : props.animal.fee
);

// HELPERS
function toggleServiceFeeOverlay(event: Event) {
  serviceFeeOverlay.value?.toggle(event);
}

// EVENTS
function onFeeAmountConfirm(payload: { amount: number; discountType: 'percent' | 'fixed'; discount?: number }) {
  const amount = payload.amount;
  const animalId = props.animal?.animal_id;
  if (!animalId) return;

  const PAYLOAD = {
    animal_id: animalId,
    new_fee: amount,
    discount_type: payload.discountType,
    ...(payload.discount != null && { discount: payload.discount }),
  };

  loading.value = true;

  EditAnimalFeeService(PAYLOAD)
    .then(({ data }) => (cartData.value = data))
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (loading.value = false));
}

function getFeeLabelKey(outcomeType: string | undefined): string {
  // prettier-ignore
  return outcomeType === 'return_to_owner' ? 'cart.summary.rtoFee' :
    outcomeType === 'adopted' ? 'cart.summary.adoptionFee' :
    outcomeType === INTAKE_TYPES.BOARDING_IN ? 'cart.summary.boardingInFee' :
    outcomeType === INTAKE_TYPES.CLINIC_IN ? 'cart.summary.clinicInFee' :
    outcomeType === INTAKE_TYPES.DOA ? 'cart.summary.doaFee' :
    outcomeType === INTAKE_TYPES.OWNER_REQUESTED_EUTHANASIA ? 'cart.summary.euthanasiaFee' :
    outcomeType === INTAKE_TYPES.OWNER_SURRENDER ? 'cart.summary.ownerSurrenderFee' :
    outcomeType === INTAKE_TYPES.RETURN ? 'cart.summary.returnFee' :
    outcomeType === INTAKE_TYPES.SEIZED ? 'cart.summary.seizedFee' :
    outcomeType === INTAKE_TYPES.SERVICE_IN ? 'cart.summary.serviceInFee' :
    outcomeType === INTAKE_TYPES.STRAY ? 'cart.summary.strayFee' :
    outcomeType === INTAKE_TYPES.TRANSFER_IN ? 'cart.summary.transferInFee' :
    outcomeType === INTAKE_TYPES.WILDLIFE_IN ? 'cart.summary.wildlifeInFee' :
    outcomeType === INTAKE_TYPES.TNVR ? 'cart.summary.tnvrFee' :
    'cart.summary.intakeFee';
}
</script>

<template>
  <div>
    <AShoppingCartAnimal
      nameSize="text-lg"
      size="xl"
      type="horizontal"
      :animal
    />

    <div class="mt-2 flex flex-col gap-1 text-sm">
      <AShoppingCartEditAmount
        v-if="!hideFeeAndPromo"
        ref="editAmount"
        :currentAmount="feeAmountNumber"
        :isEditingAnimals
        :label="feeLabelKey"
        :originalAmount="feeOriginalForDisplay"
        @onUpdateRequest="onFeeAmountConfirm"
      />

      <div
        v-if="parseFloat(animal.service_fee) > 0"
        class="flex h-8 items-center justify-between gap-1"
      >
        <span class="flex items-center gap-1">
          {{ $t('cart.summary.serviceFee') }}

          <span
            class="cursor-help"
            @click="toggleServiceFeeOverlay($event)"
            @mouseenter="toggleServiceFeeOverlay($event)"
            @mouseleave="toggleServiceFeeOverlay($event)"
          >
            <i class="pi pi-info-circle text-surface-500 text-xs" />
          </span>

          <OverlayPanel
            class="max-w-[280px] text-sm"
            ref="serviceFeeOverlay"
          >
            <p class="text-surface-700 dark:text-surface-300 p-0">
              {{ $t('cart.summary.serviceFeeTooltip') }}
            </p>
          </OverlayPanel>
        </span>

        <span :class="['text-right font-medium', isEditingAnimals ? 'pr-12' : '']">
          {{ applyShelterCurrency(animal.service_fee) }}
        </span>
      </div>

      <AShoppingCartDiscountSelector
        v-if="!hideFeeAndPromo"
        :animal
        :isEditingAnimals
        @onShoppingCartDiscountSelectorOpening="editAmountRef?.closeEdit?.()"
      />
    </div>

    <slot name="medical" />

    <Divider
      v-if="!isLastAnimal"
      class="before:border-t-1 absolute left-0"
    />
  </div>
</template>
